import { useTheme } from '../App';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const sections = [
  { id: 'permitted-use', title: '1. Permitted Use' },
  { id: 'prohibited-activities', title: '2. Prohibited Activities' },
  { id: 'user-content', title: '3. User Content' },
  { id: 'account-security', title: '4. Account Security' },
  { id: 'api-usage', title: '5. API Usage' },
  { id: 'enforcement', title: '6. Enforcement' },
  { id: 'reporting', title: '7. Reporting Violations' },
  { id: 'changes', title: '8. Changes to This Policy' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.04, duration: 0.5, ease: 'easeOut' } }),
};

export default function UsagePolicyPage() {
  const { theme } = useTheme();
  const isDark = theme !== 'aurora';
  const { t } = useTranslation();

  const card = isDark
    ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border border-white/[0.05]'
    : 'bg-white border border-gray-200 shadow-sm';
  const heading = isDark ? 'text-white' : 'text-gray-900';
  const body = isDark ? 'text-white/60' : 'text-gray-600';
  const bodyStrong = isDark ? 'text-white/80' : 'text-gray-700';

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={`min-h-screen transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
      {isDark && (
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/3 w-[500px] h-[500px] bg-rose-500/5 rounded-full blur-[120px]" />
        </div>
      )}

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-24 sm:py-32">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500 bg-clip-text text-transparent">
              Acceptable Use Policy
            </span>
          </h1>
          <p className={`text-lg ${body}`}>
            Guidelines for responsible and lawful use of the myAct platform.
          </p>
          <p className={`text-sm mt-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
            Last updated: March 1, 2026
          </p>
        </motion.div>

        {/* Table of Contents */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className={`rounded-2xl p-6 sm:p-8 mb-10 ${card}`}
        >
          <h2 className={`font-['Outfit'] text-xl font-semibold mb-4 ${heading}`}>Table of Contents</h2>
          <nav className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`text-left text-sm px-3 py-2 rounded-lg transition-colors ${
                  isDark
                    ? 'text-white/60 hover:text-white hover:bg-white/[0.05]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {s.title}
              </button>
            ))}
          </nav>
        </motion.div>

        {/* Sections */}
        <div className="space-y-8">
          <motion.div id="permitted-use" custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>1. Permitted Use</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>The myAct platform is designed to help users exercise their rights under the EU Digital Services Act. You may use the platform for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Submitting legitimate appeals against content moderation decisions made by online platforms subject to the DSA</li>
                <li>Managing and tracking the progress of your submitted appeals</li>
                <li>Uploading supporting documentation relevant to your appeals</li>
                <li>Communicating with our support team regarding your cases</li>
                <li>Accessing educational resources about your digital rights under the DSA</li>
                <li>Managing your account settings, subscription, and payment information</li>
              </ul>
              <p>You agree to use the Service only for lawful purposes and in accordance with these guidelines, our Terms of Service, and all applicable laws and regulations.</p>
            </div>
          </motion.div>

          <motion.div id="prohibited-activities" custom={1} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>2. Prohibited Activities</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>The following activities are strictly prohibited on the myAct platform:</p>

              <h3 className={`font-['Outfit'] text-lg font-medium mt-6 mb-2 ${bodyStrong}`}>a) Fraudulent & Deceptive Conduct</h3>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Submitting appeals based on false, misleading, or fabricated information</li>
                <li>Impersonating another person or misrepresenting your identity or affiliation</li>
                <li>Creating multiple accounts to circumvent platform limitations or enforcement actions</li>
                <li>Filing appeals for accounts or content that you do not own or are not authorized to represent</li>
                <li>Manipulating the appeal process for competitive advantage or commercial gain unrelated to legitimate rights</li>
              </ul>

              <h3 className={`font-['Outfit'] text-lg font-medium mt-6 mb-2 ${bodyStrong}`}>b) Harmful & Illegal Content</h3>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Uploading content that is illegal under EU or member state law</li>
                <li>Submitting content that promotes violence, hatred, discrimination, or terrorism</li>
                <li>Sharing content that contains child sexual abuse material (CSAM)</li>
                <li>Uploading malware, viruses, or other harmful code</li>
                <li>Distributing unsolicited commercial communications (spam)</li>
              </ul>

              <h3 className={`font-['Outfit'] text-lg font-medium mt-6 mb-2 ${bodyStrong}`}>c) Platform Abuse</h3>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Attempting to gain unauthorized access to the platform, other users' accounts, or our infrastructure</li>
                <li>Interfering with or disrupting the integrity or performance of the Service</li>
                <li>Using automated tools (bots, scrapers, crawlers) to access or interact with the Service without authorization</li>
                <li>Reverse engineering, decompiling, or attempting to extract the source code of the Service</li>
                <li>Circumventing or attempting to circumvent any security measures, rate limits, or access controls</li>
                <li>Reselling, sublicensing, or commercially exploiting the Service without written permission</li>
              </ul>

              <h3 className={`font-['Outfit'] text-lg font-medium mt-6 mb-2 ${bodyStrong}`}>d) Abuse of the Appeal System</h3>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Filing mass frivolous appeals intended to overload the system or target platforms</li>
                <li>Using the platform to harass platform employees or moderators</li>
                <li>Submitting duplicate appeals for the same issue without material new evidence</li>
                <li>Using the appeal process to further illegal activities or evade lawful content moderation</li>
              </ul>
            </div>
          </motion.div>

          <motion.div id="user-content" custom={2} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>3. User Content</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>When submitting content to the myAct platform, including documents, screenshots, descriptions, and other materials, you agree to the following:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><span className={bodyStrong}>Ownership:</span> You retain ownership of all content you submit. By uploading content, you grant myAct a limited licence to use it solely for processing your appeal</li>
                <li><span className={bodyStrong}>Accuracy:</span> All submitted content must be accurate, truthful, and directly relevant to your appeal</li>
                <li><span className={bodyStrong}>Third-Party Rights:</span> You must not upload content that infringes on the intellectual property rights, privacy rights, or other rights of third parties</li>
                <li><span className={bodyStrong}>Sensitive Data:</span> Avoid including unnecessary sensitive personal data (e.g., national ID numbers, financial account details) in your submissions. Only include information directly relevant to your appeal</li>
                <li><span className={bodyStrong}>Retention:</span> Uploaded content is retained in accordance with our Privacy Policy. You may request deletion of your content at any time, subject to legal retention requirements</li>
                <li><span className={bodyStrong}>Content Review:</span> We reserve the right to review submitted content to ensure compliance with this policy and to remove content that violates these guidelines</li>
              </ul>
            </div>
          </motion.div>

          <motion.div id="account-security" custom={3} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>4. Account Security</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>Maintaining the security of your account is a shared responsibility. You are required to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Use a strong, unique password that you do not share with anyone or reuse across other services</li>
                <li>Enable two-factor authentication (2FA) when available for enhanced security</li>
                <li>Keep your login credentials confidential and not share your account access with unauthorized individuals</li>
                <li>Log out of your account when using shared or public devices</li>
                <li>Immediately notify us at security@arbbio.com if you suspect unauthorized access to your account</li>
                <li>Regularly review your account activity and report any suspicious actions</li>
              </ul>
              <p>We implement industry-standard security measures to protect your account, including encryption, secure authentication protocols, and regular security audits. However, no system is completely immune to threats, and you acknowledge the inherent risks of online services.</p>
            </div>
          </motion.div>

          <motion.div id="api-usage" custom={4} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>5. API Usage</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>If myAct provides API access, the following additional rules apply:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><span className={bodyStrong}>Authentication:</span> All API requests must be properly authenticated using valid API keys or OAuth tokens issued to your account</li>
                <li><span className={bodyStrong}>Rate Limits:</span> You must respect the published rate limits for API endpoints. Exceeding rate limits may result in temporary or permanent suspension of API access</li>
                <li><span className={bodyStrong}>Permitted Use:</span> API access is granted solely for integrating with the myAct platform for legitimate appeal management purposes</li>
                <li><span className={bodyStrong}>Prohibited Use:</span> You may not use the API to scrape data, build competing services, perform denial-of-service attacks, or any other purpose outside the intended scope</li>
                <li><span className={bodyStrong}>Data Handling:</span> Any data retrieved through the API must be handled in compliance with GDPR and our Privacy Policy</li>
                <li><span className={bodyStrong}>Key Security:</span> API keys are confidential. You must not share, publish, or embed API keys in client-side code or public repositories</li>
              </ul>
              <p>We reserve the right to modify, throttle, or discontinue API access at any time with reasonable notice. Abuse of API access is subject to the enforcement measures described in Section 6.</p>
            </div>
          </motion.div>

          <motion.div id="enforcement" custom={5} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>6. Enforcement</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>Violations of this Acceptable Use Policy may result in the following enforcement actions, depending on the severity and frequency of the violation:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><span className={bodyStrong}>Warning:</span> A formal notification identifying the violation and requesting corrective action</li>
                <li><span className={bodyStrong}>Content Removal:</span> Removal of content or submissions that violate this policy</li>
                <li><span className={bodyStrong}>Feature Restriction:</span> Temporary restriction of certain features or capabilities on your account</li>
                <li><span className={bodyStrong}>Account Suspension:</span> Temporary suspension of your account pending investigation or corrective action</li>
                <li><span className={bodyStrong}>Account Termination:</span> Permanent closure of your account for serious or repeated violations</li>
                <li><span className={bodyStrong}>Legal Action:</span> Referral to law enforcement authorities for violations involving illegal activity</li>
              </ul>
              <p>Before taking enforcement action (except in cases requiring immediate action for safety or legal reasons), we will:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Notify you of the suspected violation and the specific clause of this policy that was breached</li>
                <li>Provide you with an opportunity to respond and present your case</li>
                <li>Consider the context, severity, and any mitigating factors before reaching a decision</li>
              </ul>
              <p>You may appeal any enforcement decision by contacting appeals@arbbio.com within 14 days of notification.</p>
            </div>
          </motion.div>

          <motion.div id="reporting" custom={6} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>7. Reporting Violations</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>We rely on our community to help maintain a safe and trustworthy platform. If you become aware of any activity that violates this Acceptable Use Policy, please report it promptly:</p>
              <div className={`rounded-xl p-5 mt-4 ${isDark ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                <p className={`font-medium ${bodyStrong}`}>Report a Violation</p>
                <p className="mt-2">Email: abuse@arbbio.com</p>
                <p>In-Platform: Use the "Report" feature available on relevant pages</p>
              </div>
              <p className="mt-4">When reporting a violation, please include:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>A description of the suspected violation</li>
                <li>The specific section of this policy you believe was violated</li>
                <li>Any relevant evidence or screenshots</li>
                <li>The date and time of the observed activity, if applicable</li>
              </ul>
              <p>All reports are reviewed confidentially. We will not disclose the identity of reporters except as required by law. We are committed to investigating all reports promptly and taking appropriate action.</p>
            </div>
          </motion.div>

          <motion.div id="changes" custom={7} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>8. Changes to This Policy</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>We may update this Acceptable Use Policy from time to time. When we make changes:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>We will provide at least 14 days' notice before changes take effect for minor updates, and 30 days for material changes</li>
                <li>We will notify you via email and/or a prominent notice on the platform</li>
                <li>We will update the "Last Updated" date at the top of this policy</li>
              </ul>
              <p>Your continued use of the Service after the effective date of any changes constitutes your acceptance of the updated policy. If you do not agree with the changes, you should discontinue use of the Service.</p>
              <div className={`rounded-xl p-5 mt-6 ${isDark ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                <p className={`font-medium ${bodyStrong}`}>Questions about this policy?</p>
                <p className="mt-2">Email: legal@arbbio.com</p>
                <p>Support: support@arbbio.com</p>
                <p>Website: https://arbbio.com/contact</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
